// src/modules/fire/Quotation/hooks/useFireCalculation.ts

import { useCallback, useState, useRef, useEffect } from "react";
import { FireServices } from "../../../../../shared/services/api/fire/FireServices";
import { showToastHelper } from "../../../quote/components/utils/showToastHelper";
import { mapFormToCalculationPayload } from "../../utils/mapCalculationPayload";
import { globalRemoveMaskReal2 } from "../../../../../shared/utils";

interface UseFireCalculationParams {
	mode: "create" | "edit";

	formRef: React.RefObject<any>;
	toastHandler: any;

	clientId: string | null;
	companyId: string;
	propertyType: string;
	occupationId: string;
	assistanceId: string | null;

	rentValue: string;
	prolaboreValue: string; // 🆕
	coverages: any[];
	setCoverages?: (v: any[]) => void;
	brokerageIdCode: string;
	brokerageComissionPercent: number;
	fireQuoteData: any | null;
	loadingCoverages?: boolean;
	loadingData?: boolean;
	coverageVersion?: number;
	predialPercent?: string;
	contentPercent?: string;
}

export function useFireCalculation({
	mode,
	formRef,
	toastHandler,

	clientId,
	companyId,
	propertyType,
	occupationId,
	assistanceId,

	rentValue,
	prolaboreValue,
	coverages,
	setCoverages,
	brokerageIdCode,
	brokerageComissionPercent,
	fireQuoteData,
	loadingCoverages = false,
	loadingData = false,
	coverageVersion = 0,
	predialPercent = '80',
	contentPercent = '20',
}: UseFireCalculationParams) {
	const [loadingCalc, setLoadingCalc] = useState(false);
	const [result, setResult] = useState<any>(null);
	const [calcError, setCalcError] = useState(false); // 🆕 Rastreia erro de cálculo

	const lastSig = useRef<string | null>(null);
	const lastValidCoveragesRef = useRef<any[]>([]); // 🆕 Snapshot das coberturas válidas
	const editLoadedRef = useRef(false); // ⭐ impede cálculo no carregamento
	const lastCalculatedRentRef = useRef<number | null>(null); // 🆕 Rastreia último aluguel calculado
	const initCompleteRef = useRef(false); // 🆕 Marca quando inicialização está completa
	const coverageVersionRef = useRef(0); // 🆕 Versão das coberturas (incrementa em mudanças manuais)

	// ================================================================
	// 1. EDIT → Carrega valores do backend
	// ================================================================
	useEffect(() => {
		if (mode !== "edit") return;
		if (!fireQuoteData) return;
		if (editLoadedRef.current) return;

		setResult({
			totalQuotation: Number(fireQuoteData.insurance_prize ?? 0),
			discount: Number(fireQuoteData.discount ?? 0),
			installments: Number(fireQuoteData.installment_number ?? 1),
			coverages: fireQuoteData.coverages ?? [],
		});

		// Inicializa com o valor salvo no banco
		lastCalculatedRentRef.current = Number(fireQuoteData.rent_value ?? 0);

		editLoadedRef.current = true;
	}, [mode, fireQuoteData]);

	// ================================================================
	// 1b. EDIT → Marca inicialização como completa após dados estáveis
	// ================================================================
	useEffect(() => {
		if (mode !== "edit") {
			initCompleteRef.current = true; // CREATE sempre pronto
			return;
		}

		// Aguarda: dados carregados, coberturas carregadas, e loading finalizado
		if (editLoadedRef.current && !loadingCoverages && !loadingData && coverages.length > 0) {
			// Pequeno delay para garantir que tudo estabilizou
			const timer = setTimeout(() => {
				initCompleteRef.current = true;
			}, 100);
			return () => clearTimeout(timer);
		}
	}, [mode, editLoadedRef.current, loadingCoverages, loadingData, coverages.length]);

	// ================================================================
	// 2. Checagem central — garante condições mínimas para calcular
	// ================================================================
	function canCalculate(form: any) {
		if (!clientId) return false;
		if (!companyId) return false;
		if (!propertyType) return false;
		if (!occupationId) return false;
		if (!assistanceId) return false;

		// aluguel válido
		const rent = Number(globalRemoveMaskReal2(rentValue ?? ""));
		if (rent <= 0) return false;

		// coberturas carregadas
		if (!coverages || coverages.length === 0) return false;

		// form válido
		if (!form) return false;

		return true;
	}

	// ================================================================
	// 3. Detecta alterações reais no modo EDIT
	// ================================================================
	function editHasChanges(form: any) {
		if (mode !== "edit") return false;
		if (!editLoadedRef.current) return false;
		if (!initCompleteRef.current) return false; // 🆕 Aguarda inicialização completa

		// Comparação robusta de valores numéricos
		const currentRent = Number(globalRemoveMaskReal2(rentValue ?? "0"));
		const savedRent = Number(fireQuoteData?.rent_value ?? 0);

		const currentProlabore = Number(globalRemoveMaskReal2(prolaboreValue ?? "0"));
		const savedProlabore = Number(fireQuoteData?.prolabore_percent_amount ?? 0);

		if (currentRent !== savedRent) return true;
		if (currentProlabore !== savedProlabore) return true;

		if (form.propertyType !== fireQuoteData?.occupation?.riskGroup?.id) return true;
		if (form.occupationId !== fireQuoteData?.occupationId) return true;
		if (form.assistanceId !== fireQuoteData?.assistanceId) return true;

		// Comparação robusta de coberturas (ignora campos extras como 'source', 'name', etc)
		const simplify = (list: any[]) => list.map(c => ({
			id: c.id,
			enabled: !!c.enabled,
			value: Number(c.coverage_value ?? 0)
		})).sort((a, b) => a.id.localeCompare(b.id));

		const currentCovs = simplify(coverages);
		const savedCovs = simplify(fireQuoteData?.coverages ?? []);

		return JSON.stringify(currentCovs) !== JSON.stringify(savedCovs);
	}

	// ================================================================
	// 4. Função de cálculo principal
	// ================================================================
	const calculate = useCallback(
		async (form: any) => {
			if (!canCalculate(form)) return;

			// EDIT → só calcula depois do carregamento
			if (mode === "edit" && !editLoadedRef.current) return;

			const currentRent = Number(globalRemoveMaskReal2(rentValue ?? "0"));
			const rentChanged = lastCalculatedRentRef.current !== currentRent;

			const payload = mapFormToCalculationPayload(form, {
				clientId: clientId!,
				companyId,
				propertyType,
				occupationId,
				assistanceId,
				brokerageId: String(fireQuoteData?.brokerageId ?? fireQuoteData?.brokerage_code ?? brokerageIdCode ?? ""),
				pc_predial: Number(predialPercent.replace(",", ".") || 80),
				pc_content: Number(contentPercent.replace(",", ".") || 20),
				prolabore_percent_amount: Number(globalRemoveMaskReal2(prolaboreValue ?? "0")),
				brokerage_comission_percent_amount: Number(brokerageComissionPercent ?? fireQuoteData?.brokerage_comission_percent_amount ?? 0),
				insurance_prize: Number(result?.totalQuotation ?? 0),
				flag_rent_changed: rentChanged,
				coverages: coverages.filter(c => c.enabled !== false), // 🛡️ Filtra desabilitadas

			});

			// Atualiza o último aluguel calculado
			lastCalculatedRentRef.current = currentRent;

			// Evitar chamadas duplicadas
			const sig = JSON.stringify(payload);
			if (sig === lastSig.current) return;
			lastSig.current = sig;

			// 🆕 Salvar snapshot das coberturas antes do cálculo
			const coveragesSnapshot = [...coverages];

			try {
				setLoadingCalc(true);
				setCalcError(false);
				const response = await FireServices.FireQuotation(payload);
				const data = response?.data ?? response;

				setResult({
					totalQuotation: Number(data.insurance_values ?? 0),
					discount: Number(data.discount ?? 0),
					installments: Number(data.installments ?? 1),
					coverages: data.coverages ?? [],
				});

				// 🆕 Atualizar snapshot de coberturas válidas após sucesso
				lastValidCoveragesRef.current = [...coverages];
			} catch (err) {
				console.error(err);
				setCalcError(true);
				showToastHelper(toastHandler, "Erro ao calcular cotação.", "error");

				// 🆕 REVERTER coberturas para o snapshot anterior em caso de erro
				if (setCoverages) {
					setCoverages(coveragesSnapshot);
				}
			} finally {
				setLoadingCalc(false);
			}
		},
		[mode, clientId, companyId, propertyType, occupationId, assistanceId, coverages, fireQuoteData, result, rentValue, prolaboreValue, predialPercent, contentPercent]
	);

	// ================================================================
	// 5. Controller automático (com Debounce)
	// ================================================================
	useEffect(() => {
		// Se as coberturas ainda estão carregando, não calcula
		if (loadingCoverages) return;
		// Se os dados da cotação (edit) ainda estão carregando, não calcula
		if (loadingData) return;
		// Se a inicialização não está completa, não calcula
		if (!initCompleteRef.current) return; // 🆕

		const timeoutId = setTimeout(() => {
			//console.log('🔥 DISPARANDO CÁLCULO - coverageVersion:', coverageVersion, 'rentValue:', rentValue);
			const form = formRef.current?.getData?.();
			if (!form) return;

			// CREATE → sempre calcular quando tiver base
			if (mode === "create") {
				if (!canCalculate(form)) return;
				calculate(form);
				return;
			}

			// EDIT → calcula somente quando algo realmente muda
			if (mode === "edit") {
				if (!editHasChanges(form)) return;
				if (!canCalculate(form)) return;
				calculate(form);
			}
		}, 500); // ⏳ 500ms de debounce

		return () => clearTimeout(timeoutId);
	}, [mode, clientId, companyId, propertyType, occupationId, assistanceId, rentValue, prolaboreValue, fireQuoteData, loadingCoverages, loadingData, coverageVersion]);

	return {
		result,
		loadingCalc,
		calculate,
		calcError, // 🆕 Expor estado de erro
	};
}

